import i18n from "i18next";
import { useState } from "react";
import { IconSparkles } from "@tabler/icons-react";
import { testIds } from "@/testIds";

export interface AskEdgeTabProps {
  /** Lilia is working (editing the document) in the other window. */
  working: boolean;
  /** Bring the other window forward; resolves false when the browser wouldn't. */
  onFocusOther: () => Promise<boolean>;
  onBringBack: () => void;
}

/**
 * While Ask Lilia is in its own window (Olivia, 10 Oct): a 32 px tab on the
 * editor's right edge, with a dot while Lilia works. A click switches to the
 * other window, or offers Bring back when the browser won't switch.
 */
export function AskEdgeTab({ working, onFocusOther, onBringBack }: AskEdgeTabProps) {
  const [offer, setOffer] = useState(false);
  return (
    <div className="relative shrink-0" style={{ width: 32 }}>
      <button
        type="button"
        data-testid={testIds.askWindow.edgeTab}
        data-working={working ? "true" : "false"}
        onClick={async () => {
          if (!(await onFocusOther())) setOffer(true);
        }}
        title={i18n.t("editor:askEdge.askLiliaIsInAnother")}
        aria-label={working ? "Ask Lilia is in another window, working" : i18n.t("editor:askEdge.askLiliaIsInAnother")}
        className="flex h-full w-full flex-col items-center justify-center gap-2 border-l border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-surface)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
      >
        <span className="relative">
          <IconSparkles size={16} className="text-[var(--lilia-accent,#6366f1)]" />
          {working && (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 h-2 w-2 animate-pulse rounded-full bg-[var(--lilia-primary,#0a76a4)]"
            />
          )}
        </span>
        <span style={{ writingMode: "vertical-rl" }} className="text-[10.5px] font-semibold tracking-wide text-[var(--lilia-text-secondary)]">
          {i18n.t("editor:askEdge.askLilia")}
        </span>
      </button>
      {offer && (
        <div
          role="dialog"
          aria-label={i18n.t("editor:askEdge.askLiliaIsInAnother")}
          className="absolute right-[36px] top-1/2 z-50 w-[220px] -translate-y-1/2 rounded-lg border border-[var(--lilia-border)] bg-[var(--lilia-surface)] p-3 text-[12.5px] shadow-lg"
        >
          <p className="mb-2 text-[var(--lilia-text-secondary)]">Ask Lilia is in another window. The browser won&apos;t switch to it from here.</p>
          <div className="flex justify-end gap-2">
            <button type="button" className="rounded px-2 py-1 text-[var(--lilia-text-muted)] hover:bg-[var(--lilia-hover)]" onClick={() => setOffer(false)}>
              {i18n.t("editor:askEdge.notNow")}
            </button>
            <button
              type="button"
              data-testid={testIds.askWindow.edgeBringBack}
              className="rounded bg-[var(--lilia-primary)] px-2 py-1 font-medium text-[var(--lilia-on-primary,#fff)]"
              onClick={() => {
                setOffer(false);
                onBringBack();
              }}
            >
              {i18n.t("editor:askEdge.bringBack")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
